/**
 * ShareLeadWhatsAppModal - Select a WhatsApp-ready provider and share lead summary
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import OutboundMessageModal from "../../../shared/components/OutboundMessageModal.jsx";
import { loadPartnerChannels } from "../../../shared/utils/outboundChannelLoaders.js";
import { PARTNER_OUTBOUND_CHANNEL_STORAGE_KEY } from "../../../shared/utils/outboundMessaging.js";
import { generateLeadWhatsAppMessage } from "../utils/whatsappLinkGenerator.js";
import { getSuggestedProvidersForLead } from "../../opportunities/services/opportunityService.js";

const ShareLeadWhatsAppModal = ({
  open,
  lead,
  onClose,
  onShared,
  suggestedProviders: suggestedProvidersProp,
}) => {
  const [suggestedProviders, setSuggestedProviders] = useState(
    suggestedProvidersProp || []
  );

  useEffect(() => {
    if (suggestedProvidersProp?.length) {
      setSuggestedProviders(suggestedProvidersProp);
      return;
    }

    const leadId = lead?.id || lead?._id;
    if (!open || !leadId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await getSuggestedProvidersForLead(leadId);
        if (!cancelled && response.success) {
          setSuggestedProviders(response.matches || []);
        }
      } catch {
        if (!cancelled) {
          setSuggestedProviders([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, lead, suggestedProvidersProp]);

  const loadChannels = useCallback(() => loadPartnerChannels(), []);

  const buildMessage = useCallback(() => {
    if (!lead) return "";
    try {
      return generateLeadWhatsAppMessage(lead);
    } catch {
      return "";
    }
  }, [lead]);

  const handleOpened = useCallback(
    (channel) => {
      onShared?.(channel);
    },
    [onShared]
  );

  const modalKey = useMemo(
    () => lead?.id || lead?._id || "lead-share",
    [lead]
  );

  const description = useMemo(() => {
    const base =
      "Search for a provider with a WhatsApp-ready phone number. Only active WhatsApp-ready partners are shown.";
    if (!suggestedProviders.length) {
      return base;
    }
    const names = suggestedProviders
      .slice(0, 5)
      .map((m) => `${m.name} (${m.phone}, score ${m.score})`)
      .join("; ");
    return `${base} Suggested for this route: ${names}.`;
  }, [suggestedProviders]);

  if (!lead) {
    return null;
  }

  return (
    <OutboundMessageModal
      key={modalKey}
      open={open}
      onClose={onClose}
      title="Share lead with provider"
      description={description}
      recipientMode="channel"
      loadChannels={loadChannels}
      buildMessage={buildMessage}
      storageKey={PARTNER_OUTBOUND_CHANNEL_STORAGE_KEY}
      channelListLabel="Send to provider"
      searchPlaceholder="Name, contact person, or phone"
      emptyChannelsMessage="No WhatsApp-ready providers found. Mark a partner phone as WhatsApp-ready in Partners."
      onOpened={handleOpened}
    />
  );
};

export default ShareLeadWhatsAppModal;
